import { ReactNode } from 'react'
import { Box, Text, zIndex } from '@factorialco/deprecated-design-system'
import { F0Button, F0Icon, F0OneIcon } from '@factorialco/f0-react'
import { Cross, Check } from '@factorialco/f0-react/icons/app'
import type { DemoTourTask } from 'modules/get_started/hooks/useDemoTourTasks'

const STORAGE_KEY = 'demo_tour_completed_tasks'

const getCompletedTasks = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item): item is string => typeof item === 'string')
  } catch {
    return []
  }
}

type Props = {
  tasks: DemoTourTask[]
  completedTasks: string[]
  onStartTask: (taskKey: string) => void
  onClose: () => void
}

const StatusIcon = ({ completed }: { completed: boolean }): ReactNode => {
  if (completed) {
    return (
      <Box
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#06838B',
          flexShrink: '0',
          marginTop: '2px',
        }}
        alignItems="center"
        justifyContent="center"
      >
        <F0Icon icon={Check} size="sm" color="#FFFFFF" />
      </Box>
    )
  }
  return (
    <Box
      style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        border: '1.5px dashed #011B4B73',
        flexShrink: '0',
        marginTop: '2px',
      }}
    />
  )
}

const DemoTourPopover = ({ tasks, completedTasks, onStartTask, onClose }: Props) => {
  const taskKeys = new Set(tasks.map((t) => t.taskKey))
  const validCompleted = new Set(completedTasks.filter((k) => taskKeys.has(k)))
  const isCompleted = (taskKey: string) => validCompleted.has(taskKey)
  const completedCount = validCompleted.size
  const totalCount = tasks.length

  return (
    <Box
      position="fixed"
      borderRadius={{ all: 'abs016' }}
      boxShadow="s200"
      background="grey000White"
      flexDirection="column"
      style={{
        bottom: '32px',
        left: '32px',
        zIndex: zIndex.popovers,
        width: '380px',
        overflow: 'hidden',
      }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        paddingLeft="s24"
        paddingRight="s16"
        paddingTop="s16"
        paddingBottom="s16"
        style={{ borderBottom: '1px solid var(--color-border-default, #E5E7EB)' }}
      >
        <Box flexDirection="row" alignItems="center" gap="s8">
          <F0OneIcon size="xs" />
          <Text.Normal size="200" weight="semibold" color="grey1000">
            See what Factorial can do · {completedCount}/{totalCount}
          </Text.Normal>
        </Box>
        <F0Button
          onClick={onClose}
          variant="ghost"
          size="sm"
          icon={Cross}
          hideLabel
          round
          label="Close"
        />
      </Box>

      <Box flexDirection="column" paddingTop="s8" paddingBottom="s8">
        {tasks.map((task) => {
          const completed = isCompleted(task.taskKey)
          return (
            <Box
              key={task.taskKey}
              flexDirection="row"
              alignItems="flexStart"
              gap="s12"
              paddingTop="s12"
              paddingBottom="s12"
              paddingLeft="s24"
              paddingRight="s24"
              cursor="pointer"
              className="hover:bg-f1-background-tertiary transition-colors duration-150"
              onClick={() => onStartTask(task.taskKey)}
            >
              <StatusIcon completed={completed} />
              <Text.Normal
                size="200"
                weight="medium"
                stroked={completed}
                color={completed ? 'grey600' : 'grey1000'}
              >
                {task.title}
              </Text.Normal>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export { getCompletedTasks }
export default DemoTourPopover
